import type { RouteObject } from 'react-router-dom';

// ----------------------------------------------------------------------
/**
 * Filesystem routing sobre src/app, estilo Next.js, para os data routers (createBrowserRouter no web, createHashRouter
 * no Electron).
 *
 * Convenções: app/index.tsx → / app/privacy.tsx → /privacy app/foo/index.tsx → /foo app/foo/[id].tsx → /foo/:id
 * app/layout.tsx → layout de TODAS as rotas (deve renderizar <Outlet />) app/foo/layout.tsx → layout das rotas dentro
 * de foo/ — layouts ANINHAM
 *
 * Todo arquivo .tsx dentro de app/ vira rota automaticamente — sem registro manual. Carregamento é lazy por rota
 * (route.lazy do react-router).
 */

type Loader = () => Promise<unknown>;

type Node = {
  layout?: Loader;
  index?: Loader;
  children: Record<string, Node>;
};

const emptyNode = (): Node => ({ children: {} });

// Glob relativo a este arquivo, não ao root do Vite: o build do Electron serve o
// frontend por node_modules/@fdm/frontend, com outro root.
const pageFiles = import.meta.glob('../app/**/*.tsx');

function buildTree(): Node {
  const root = emptyNode();

  for (const [file, loader] of Object.entries(pageFiles)) {
    const segments = file
      .replace('../app/', '')
      .replace(/\.tsx$/, '')
      .split('/');
    const name = segments.pop()!;

    let node = root;
    for (const segment of segments) {
      node = node.children[segment] ??= emptyNode();
    }

    if (name === 'layout') node.layout = loader;
    else if (name === 'index') node.index = loader;
    else (node.children[name] ??= emptyNode()).index = loader;
  }

  return root;
}

// route.lazy nativo do react-router: sem React.lazy nem Suspense manual.
const lazyRoute = (loader: Loader) => async () => ({
  Component: ((await loader()) as { default: React.ComponentType }).default
});

function toRouteObjects(node: Node): RouteObject[] {
  const routes: RouteObject[] = [];

  if (node.index) routes.push({ index: true, lazy: lazyRoute(node.index) });

  for (const [segment, child] of Object.entries(node.children)) {
    const path = segment.replace(/^\[(.+)\]$/, ':$1');
    const children = toRouteObjects(child);

    // Sem layout, react-router renderiza <Outlet /> implícito no nó do caminho.
    routes.push(child.layout ? { path, lazy: lazyRoute(child.layout), children } : { path, children });
  }

  return routes;
}

export function fsRoutes(): RouteObject[] {
  const root = buildTree();
  const routes = toRouteObjects(root);

  return root.layout ? [{ lazy: lazyRoute(root.layout), children: routes }] : routes;
}
